import { ConsumptionChartData } from '@vae/server/src/utils/arrays/types/ChartData'
import { ChartDataset } from 'chart.js'
import { useMemo } from 'react'
import { ChartModes } from '../types/types'
import {
	createConsumptionDataset,
	createExchangePricesDataset,
	createExchangePriceWithTransferPrice,
	createProductionDataset,
	createTransferPricesWithTaxDataset,
} from '../utils/datasetCreators'

interface useChartDatasetsProps {
	chartModes: ChartModes
	charts: ConsumptionChartData | undefined | null
	isLoading: boolean
	isDarkMode: boolean
	useQuarterHourlyResolution: boolean
	useBarGraph: boolean
}

export const useChartDatasets = ({
	chartModes,
	charts,
	isLoading,
	isDarkMode,
	useQuarterHourlyResolution,
	useBarGraph,
}: useChartDatasetsProps) => {
	return useMemo<ChartDataset<'bar' | 'line', number[]>[]>(() => {
		const datasets: ChartDataset<'bar' | 'line', number[]>[] = []
		if (!charts) return datasets
		if (charts.consumption) {
			datasets.push(
				createConsumptionDataset({ charts, isDarkMode, useQuarterHourlyResolution, useBarGraph })
			)
		}

		if (chartModes.exchangePrices) {
			datasets.push(
				createExchangePricesDataset({ charts, isDarkMode, useQuarterHourlyResolution, useBarGraph })
			)
		}
		if (chartModes.transferPrices && !chartModes.exchangePrices && !chartModes.production) {
			datasets.push(
				createTransferPricesWithTaxDataset({
					charts,
					isDarkMode,
					useQuarterHourlyResolution,
					useBarGraph,
				})
			)
		}

		if (chartModes.exchangePrices && chartModes.transferPrices) {
			datasets.push(
				createExchangePriceWithTransferPrice({
					charts,
					isDarkMode,
					useQuarterHourlyResolution,
					useBarGraph,
				})
			)
		}
		if (chartModes.production) {
			datasets.push(
				createProductionDataset({ charts, isDarkMode, useQuarterHourlyResolution, useBarGraph })
			)
		}

		return datasets
	}, [chartModes, charts, isLoading, isDarkMode, useBarGraph, useQuarterHourlyResolution])
}
